import type { SupervisorAuditLabelRequest, SupervisorProposalCorrect } from '@avisoma/shared';

/** One finding's label while the reviewer fills it in; `null` is a group with no answer yet. */
export type AuditForm = {
  realDiscrepancy: boolean | null;
  proposalCorrect: SupervisorProposalCorrect | null;
  usefulToLessor: boolean | null;
  note: string;
};

/** A finding without a proposal has one possible answer to the second group, so it starts chosen. */
export const initialAuditForm = (hasProposal: boolean): AuditForm => ({
  realDiscrepancy: null,
  proposalCorrect: hasProposal ? null : 'not_applicable',
  usefulToLessor: null,
  note: '',
});

/** `yes` / `no` need a proposal; `not_applicable` needs none (the server rejects the rest with 400). */
export const proposalChoiceEnabled = (hasProposal: boolean, choice: SupervisorProposalCorrect): boolean =>
  hasProposal ? choice !== 'not_applicable' : choice === 'not_applicable';

/** An answer a digit key sets: never `null`, which only an unanswered group holds. */
type Patch = { [K in 'realDiscrepancy' | 'proposalCorrect' | 'usefulToLessor']?: NonNullable<AuditForm[K]> };

/** 1/2 real discrepancy, 3/4/5 proposed value, 6/7 useful to a lessor (spec: Keyboard). */
const DIGIT_PATCHES: ReadonlyMap<string, Patch> = new Map<string, Patch>([
  ['1', { realDiscrepancy: true }],
  ['2', { realDiscrepancy: false }],
  ['3', { proposalCorrect: 'yes' }],
  ['4', { proposalCorrect: 'no' }],
  ['5', { proposalCorrect: 'not_applicable' }],
  ['6', { usefulToLessor: true }],
  ['7', { usefulToLessor: false }],
]);

/** The form after digit `key`; the same object for any other key or for a choice the finding does not allow. */
export const withDigitKey = (form: AuditForm, key: string, hasProposal: boolean): AuditForm => {
  const patch = DIGIT_PATCHES.get(key);
  if (!patch) return form;
  if (patch.proposalCorrect !== undefined && !proposalChoiceEnabled(hasProposal, patch.proposalCorrect)) return form;
  return { ...form, ...patch };
};

/** The PUT body, or `null` while a group has no answer. A blank note is left out; a note is trimmed. */
export const auditRequest = (form: AuditForm): SupervisorAuditLabelRequest | null => {
  if (form.realDiscrepancy === null || form.proposalCorrect === null || form.usefulToLessor === null) return null;
  const note = form.note.trim();
  return {
    realDiscrepancy: form.realDiscrepancy,
    proposalCorrect: form.proposalCorrect,
    usefulToLessor: form.usefulToLessor,
    ...(note ? { note } : {}),
  };
};
